import { Fragment } from 'react';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import PropTypes from 'prop-types';

const validateNumericFields = currentParams => {
  const { minimum, maximum, baseline } = currentParams || {};

  if (!minimum || !maximum || !baseline) {
    return true;
  }

  const minValue = parseFloat(minimum);
  const maxValue = parseFloat(maximum);
  const baselineValue = parseFloat(baseline);

  if (minValue > maxValue) {
    return 'minimum must be < maximum';
  }

  if (minValue > baselineValue) {
    return 'minimum must be <= baseline';
  }

  if (maxValue < baselineValue) {
    return 'maximum must be >= baseline';
  }

  return true;
};

const AspenBlocks = ({ modelData }) => {
  const {
    register,
    formState: { errors },
    watch,
    setValue,
    control,
  } = useFormContext();

  const paramsArray = useFieldArray({
    control,
    name: 'params',
  });

  const handleAddParameter = () => {
    const blocks = modelData?.blocks || [];
    const firstBlock = blocks[0];

    paramsArray.append({
      block: firstBlock?.name || '',
      parameter: '',
      path: '',
      minimum: '',
      baseline: '',
      maximum: '',
      unit: '',
      aspenTooltip: '',
    });
  };

  const handleRemoveParameter = index => {
    paramsArray.remove(index);
  };

  const handleBlockChange = index => {
    setValue(`params.${index}.parameter`, '');
    setValue(`params.${index}.path`, '');
    setValue(`params.${index}.baseline`, '');
    setValue(`params.${index}.unit`, '');
    setValue(`params.${index}.aspenTooltip`, '');
  };

  const handleParameterChange = (index, paramName, blockName) => {
    const selectedBlock = modelData?.blocks?.find(block => block.name === blockName);
    const selectedParam = selectedBlock?.params?.find(param => param.name === paramName);

    if (selectedParam) {
      setValue(`params.${index}.path`, selectedParam.path);
      setValue(`params.${index}.baseline`, selectedParam.value);
      setValue(`params.${index}.unit`, selectedParam.unit);
      setValue(`params.${index}.aspenTooltip`, selectedParam.description);
    }
  };

  const blocks = modelData?.blocks || [];
  const watchedParams = watch('params') || [];

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="font-bold text-lg mb-2">Select Editable Parameters</h3>
        <div className="text-sm text-gray-600 space-y-2">
          <p>
            The file has parsed blocks and subflowsheets, found relevant parameters, and allows you to select parameters
            for editing and sensitivity analysis.
          </p>
          <p>
            Minimum and maximum bounds for parameter values are required to ensure module performance and mitigate
            convergence issues, especially for Aspen Simulation files. Runtime errors or warnings will be displayed in
            the control panel.
          </p>
          <p>
            For more information on how to use this functionality, please consult{' '}
            <a href="#" className="text-blue-600 hover:underline">
              this section of our documentation
            </a>
            .
          </p>
        </div>
      </div>

      <div className="mt-8">
        <h4 className="font-medium mb-4">Parameter Configuration</h4>
        <div className="overflow-x-auto">
          <table className="table table-sm w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="font-semibold text-xs uppercase tracking-wide">Block</th>
                <th className="font-semibold text-xs uppercase tracking-wide">Parameter</th>
                <th className="font-semibold text-xs uppercase tracking-wide">Minimum</th>
                <th className="font-semibold text-xs uppercase tracking-wide">Baseline</th>
                <th className="font-semibold text-xs uppercase tracking-wide">Maximum</th>
                <th className="font-semibold text-xs uppercase tracking-wide">Unit</th>
                <th className="font-semibold text-xs uppercase tracking-wide">Aspen Tooltip</th>
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody>
              {paramsArray.fields.map((field, index) => (
                <Fragment key={`parameter-${index}`}>
                  <tr>
                    <td>
                      <select
                        className={`select select-bordered select-sm w-full ${errors.params?.[index]?.block ? 'select-error' : ''}`}
                        {...register(`params.${index}.block`, {
                          required: 'Block is required',
                          onChange: () => handleBlockChange(index),
                        })}
                      >
                        <option value="">Select Block</option>
                        {blocks.map(block => (
                          <option key={block.name} value={block.name}>
                            {block.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select
                        className={`select select-bordered select-sm w-full ${errors.params?.[index]?.parameter ? 'select-error' : ''}`}
                        {...register(`params.${index}.parameter`, {
                          required: 'Parameter is required',
                          onChange: e => {
                            const blockName = watch(`params.${index}.block`);
                            handleParameterChange(index, e.target.value, blockName);
                          },
                        })}
                      >
                        <option value="">Select Parameter</option>
                        {(() => {
                          const selectedBlock = blocks.find(block => block.name === watch(`params.${index}.block`));
                          return (
                            selectedBlock?.params?.map(param => (
                              <option key={param.name} value={param.name}>
                                {param.name}
                              </option>
                            )) || []
                          );
                        })()}
                      </select>
                    </td>
                    <td>
                      <input
                        type="text"
                        className={`input input-bordered input-sm w-full ${errors.params?.[index]?.minimum ? 'input-error' : ''}`}
                        placeholder="Min value"
                        {...register(`params.${index}.minimum`, {
                          required: 'Minimum value is required',
                          pattern: {
                            value: /^-?\d*\.?\d+$/,
                            message: 'Please enter a number',
                          },
                          validate: () => validateNumericFields(watchedParams[index]),
                        })}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className={`input input-bordered input-sm w-full bg-gray-100 text-gray-700 ${errors.params?.[index]?.baseline ? 'input-error' : ''}`}
                        placeholder="Baseline value"
                        readOnly
                        {...register(`params.${index}.baseline`, {
                          required: 'Baseline value is required',
                          pattern: {
                            value: /^-?\d*\.?\d+$/,
                            message: 'Please enter a number',
                          },
                          validate: () => validateNumericFields(watchedParams[index]),
                        })}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className={`input input-bordered input-sm w-full ${errors.params?.[index]?.maximum ? 'input-error' : ''}`}
                        placeholder="Max value"
                        {...register(`params.${index}.maximum`, {
                          required: 'Maximum value is required',
                          pattern: {
                            value: /^-?\d*\.?\d+$/,
                            message: 'Please enter a number',
                          },
                          validate: () => validateNumericFields(watchedParams[index]),
                        })}
                      />
                    </td>
                    <td>
                      <div className="input input-bordered input-sm w-full bg-gray-100 text-gray-700 flex items-center">
                        {watchedParams[index]?.unit}
                      </div>
                    </td>
                    <td>
                      <input
                        type="text"
                        className={`input input-bordered input-sm w-full ${errors.params?.[index]?.aspenTooltip ? 'input-error' : ''}`}
                        placeholder="Tooltip"
                        {...register(`params.${index}.aspenTooltip`)}
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        onClick={() => handleRemoveParameter(index)}
                        className="btn btn-ghost btn-sm"
                        disabled={paramsArray.fields.length === 1}
                      >
                        <TrashIcon className="h-4 w-4 text-gray-500" />
                      </button>
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={8}>
                      <div className="text-error text-xs text-center">
                        {errors?.params?.[index]?.minimum?.message ||
                          errors?.params?.[index]?.baseline?.message ||
                          errors?.params?.[index]?.maximum?.message ||
                          errors?.params?.[index]?.unit?.message}
                      </div>
                    </td>
                  </tr>
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-center mt-4">
          <button type="button" onClick={handleAddParameter} className="btn btn-ghost btn-sm gap-2">
            <PlusIcon className="h-4 w-4" />
            Add Parameter
          </button>
        </div>
      </div>
    </div>
  );
};

AspenBlocks.propTypes = {
  modelData: PropTypes.shape({
    blocks: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        params: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string.isRequired,
            path: PropTypes.string.isRequired,
            unit: PropTypes.string.isRequired,
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            description: PropTypes.string.isRequired,
          }),
        ).isRequired,
      }),
    ).isRequired,
  }),
};

export default AspenBlocks;
